import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);
import { PinnedSectionDefaults } from "../base";
import type { AnimationActions, BackgroundScrollTriggerProps } from "../types";
import {
  isFirst,
  isImmediateBgControl,
  sendBgControlEvent,
  sendTextToHeaderEvent,
} from "./helpers";
import { getStepRange } from "../helpers";

const enterProps = {
  y: 0,
  opacity: 1,
  scale: 1,
  duration: 0.4,
};
const leaveProps = { opacity: 0, scale: 1, duration: 0.5 };
const leaveScaleProps = {
  opacity: 0,
  y: "-50vh",
  scale: 0.2,
  duration: 0.4,
};

type FadeVerticalAnimationActions = Required<AnimationActions>;

const animation: FadeVerticalAnimationActions = {
  onEnter: ({ section }) => {
    gsap.to(section, {
      ...enterProps,
      overwrite: "auto",
    });
  },
  onEnterBack: ({ section }) => {
    gsap.to(section, {
      ...enterProps,
      overwrite: "auto",
    });
  },
  onLeave: ({ section }) => {
    gsap.to(section, {
      y: "-100vh",
      ...leaveProps,
      overwrite: "auto",
    });
  },
  onLeaveBack: ({ section, triggerElement }) => {
    const isFirstSlide = !!triggerElement && isFirst(section, triggerElement);

    if (isFirstSlide) {
      gsap.set(section, {
        opacity: 1,
        y: 0,
        scale: 1,
      });
      return;
    }

    gsap.to(section, {
      y: "100vh",
      opacity: 0,
      scale: 1,
      duration: 0.5,
      overwrite: "auto",
    });
  },
};

const scaleAnimation: Required<Pick<AnimationActions, "onLeave">> = {
  onLeave: ({ section }) => {
    gsap.to(section, {
      ...leaveScaleProps,
      overwrite: "auto",
    });
  },
};

export const FadeVerticalScrollTrigger = ({
  section,
  step,
  triggerElement,
  sendTextToHeader,
  bgControl,
}: BackgroundScrollTriggerProps) => {
  const firstSlide = isFirst(section, triggerElement);
  const text = section.querySelector(".copy__inner") as HTMLElement;
  if (firstSlide) {
    gsap.set(section, {
      opacity: 1,
      y: 0,
      scale: 1,
    });
  } else {
    gsap.set(section, {
      opacity: 0,
      y: "100vh",
      scale: 1,
    });
  }

  const range = getStepRange(step);
  const st = ScrollTrigger.create({
    ...PinnedSectionDefaults,
    pin: false,
    trigger: triggerElement,
    start: () => range.start,
    end: () => range.end,
    onLeave: (self) => {
      if (isImmediateBgControl(bgControl)) {
        sendBgControlEvent(triggerElement, step, bgControl, "in");
      }
      if (sendTextToHeader && text) {
        scaleAnimation.onLeave({ self, section, step, triggerElement });
        sendTextToHeaderEvent(text, "add");
      } else {
        animation.onLeave({ self, section, step, triggerElement });
      }
    },
    onLeaveBack: (self) =>
      animation.onLeaveBack({ self, section, step, triggerElement }),
    onEnter: (self) => {
      animation.onEnter({ self, section, step, triggerElement });
    },
    onEnterBack: (self) => {
      animation.onEnterBack({ self, section, step, triggerElement });
      if (sendTextToHeader && text) sendTextToHeaderEvent(text, "remove");

      if (isImmediateBgControl(bgControl)) {
        sendBgControlEvent(triggerElement, step, bgControl, "out");
      }
    },
  });
  return st;
};
